import "@/components/Onboarding/Steps/NumberPorting/NumberPorting.scss";

import { captureMessage, withScope } from "@sentry/react";
import clsx from "clsx";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Accordion } from "@/components/Interface/Accordion/Accordion";
import { Button } from "@/components/Interface/Button/Button";
import { Combobox } from "@/components/Interface/FormFields/Combobox/Combobox";
import { DateInput } from "@/components/Interface/FormFields/DateInput/DateInput";
import { ErrorMessage } from "@/components/Interface/FormFields/ErrorMessage/ErrorMessage";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { RadioButton } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { ButtonGroupFlexBox } from "@/components/Layout/ButtonGroupFlexBox/ButtonGroupFlexBox";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { Step } from "@/components/Onboarding/Steps/Step";
import { useAlert } from "@/hooks/useAlert";
import { useIsInsidePortal } from "@/hooks/useIsInsidePortal";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import { useWizardBasePath } from "@/hooks/useWizard";
import { DataContext } from "@/provider/DataContextProvider";
import {
  useCheckMsisdnAvailability,
  useCompleteMnpData,
  useGetCustomer,
  useGetOnpProviders,
  useSetMnpData,
} from "@/services/api";
import { CompleteMnpData, MnpData, Provider } from "@/services/model";
import { onboardingAnalytics } from "@/utils/analytics/onboardingAnalytics";
import { AlertTypes } from "@/utils/atoms";
import { PHONE_REGEX } from "@/utils/constants";
import { getEntry } from "@/utils/dataContextHelpers";
import {
  deviceNameToValueMap,
  getDeviceDescription,
} from "@/utils/deviceUtils";
import { log } from "@/utils/log";
import { extractAndParseJSON } from "@/utils/stringUtils";

export type NumberPortingFormProps = {
  currentNumber: string;
  currentProvider: string;
  mnpContractType: "1" | "2"; // mnpContractType: id 1 = postpaid | id 2 = prepaid
  mnpTime: "2" | "3"; // mnpTime: id 2 = after the contract ends | id 3 = premature contract termination
  mnpWishDate?: string;
  enableNumberPorting: "keep" | "new";
};

// mnpContractType: id 1 = postpaid | id 2 = prepaid
const mnpContractType = {
  postpaid: { id: 1, description: "postpaid" },
  prepaid: { id: 2, description: "prepaid" },
};

// id 2 = after the contract ends | id 3 = premature contract termination
const mnpTime = {
  after: { id: 2, description: "after" },
  before: { id: 3, description: "before" },
};

const numberPrefix = "+41 (0) ";
const SWYPE_PREPAID_PROVIDER_ID = 32;
const SWYPE_PREPAID_PROVIDER_NAME = "swype prepaid";

export const formatNumber = (input: string, oldInput = "") => {
  if (input.indexOf(numberPrefix) !== 0) {
    return oldInput;
  } else {
    const match = input.match(PHONE_REGEX);
    const formatted = match
      ? match
          .slice(1)
          .filter((x) => x)
          .join(" ")
      : input;

    return formatted.substring(0, 32);
  }
};

const providersToObj = (arr: Provider[]): Record<string, number> =>
  Object.fromEntries(
    arr
      .filter(({ providerName, id }) => providerName && id)
      .map(({ providerName, id }) => [providerName, id]),
  );

// Format MSISDN for API: strip +41 prefix and return 10 digits starting with 0 (no spaces)
const formatMsisdnForApi = (input: string): string => {
  const withoutIntl = input.replace(/^(\+41\s*\(0\)\s*|\+41\s*)/, "");
  const digits = withoutIntl.replace(/\D/g, "");
  const msisdn = digits.startsWith("0") ? digits : `0${digits}`;

  return msisdn.slice(0, 10);
};

export const NumberPorting: FC<{ missingInfo?: boolean }> = ({
  missingInfo,
}) => {
  const { dataContext, setDataContext } = useContext(DataContext);
  const basePath = useWizardBasePath();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [msisdnValidationStatus, setMsisdnValidationStatus] = useState<
    "idle" | "loading" | "valid" | "invalid"
  >("idle");
  const [msisdnError, setMsisdnError] = useState<string>("");
  const [simId] = useSimIdFromQueryParam();

  const { data, isFetching: isLoadingProviders } = useGetOnpProviders();
  const { data: customerData } = useGetCustomer();
  const providers = useMemo(() => data && providersToObj(data), [data]);

  // MSISDN validation
  const { mutate: checkMsisdnAvailability } = useCheckMsisdnAvailability({
    mutation: {
      onSuccess: (data) => {
        if (data.msisdnStatus === "ALREADY_IN_USE") {
          setMsisdnValidationStatus("invalid");
          setMsisdnError(
            t("Onboarding.sections.number-porting.msisdnAlreadyInUse"),
          );
        } else if (data.msisdnStatus === "SUSPENDED") {
          setMsisdnValidationStatus("invalid");
          setMsisdnError(
            t("Onboarding.sections.number-porting.msisdnSuspended"),
          );
        } else if (data.msisdnStatus === "AVAILABLE") {
          setMsisdnValidationStatus("valid");
          setMsisdnError("");
        } else {
          setMsisdnValidationStatus("invalid");
          setMsisdnError(t("Common.generalForm.error.genericError"));
        }
      },
      onError: (error: any) => {
        setMsisdnValidationStatus("invalid");

        const errorStatus = error?.response?.status;
        const errorData = error?.response?.data;

        // Check for 400 Bad Request
        if (errorStatus === 400) {
          // The error data is a string that contains the JSON response
          // Format: "400 Bad Request: \"{\"code\":400,\"msisdnStatus\":\"ALREADY_IN_USE\",\"message\":\"SIM ist bereits aktiv ausgespielt\"}\""
          const parsedData = extractAndParseJSON(errorData);

          if (parsedData?.msisdnStatus === "ALREADY_IN_USE") {
            setMsisdnError(
              t("Onboarding.sections.number-porting.msisdnAlreadyInUse"),
            );

            return;
          }

          // Fallback: check if the error message contains the specific text
          if (
            typeof errorData === "string" &&
            errorData.includes("SIM ist bereits aktiv ausgespielt")
          ) {
            setMsisdnError(
              t("Onboarding.sections.number-porting.msisdnAlreadyInUse"),
            );

            return;
          }
        }

        setMsisdnError(t("Common.generalForm.error.genericError"));
      },
    },
  });

  const isInsidePortal = useIsInsidePortal();
  const showAlert = useAlert();

  const { mutate: setMnpData, isLoading: isSettingMnpData } = useSetMnpData({
    mutation: {
      onSuccess: (data) => {
        setDataContext({
          ...dataContext,
          basket: data,
          numberPorting: formValues,
          allowedMaxOnboardingStep: 4,
        });
        navigate(
          `${basePath}/4/${isInsidePortal ? "checkout" : "personal-details"}`,
        );
      },
      onError: (error: any) => {
        // Handle "invalid phone number" error - following the same pattern as PersonalDataForm
        if (`${error?.response?.data}`.includes("invalid phone number")) {
          setError("currentNumber", {
            type: "backendError",
            message: t("translation:label.validation.phoneBackend"),
          });
        } else {
          // Handle other errors
          showAlert({
            text: t("Common.generalForm.error.genericError"),
            type: AlertTypes.error,
          });
        }
      },
    },
  });

  const { mutate: completeMnpData } = useCompleteMnpData({
    mutation: {
      onSuccess: () => {
        showAlert({
          text: t("Onboarding.sections.number-porting.completed"),
          type: AlertTypes.success,
        });
        navigate("/portal/cockpit", {
          replace: true,
        });
      },
      onError: () => {
        showAlert({
          text: t("translation:Common.generalForm.error.genericError"),
          type: AlertTypes.error,
        });
      },
    },
  });

  const {
    register,
    watch,
    control,
    resetField,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm<NumberPortingFormProps>({
    mode: "onChange",
    defaultValues: dataContext.numberPorting,
  });

  const oldNumber = watch("currentNumber");
  const formValues = watch();
  const entry = getEntry(dataContext);
  const device = getDeviceDescription(entry?.device?.description);
  const enableNumberPortingValue = watch("enableNumberPorting");
  const currentProvider = watch("currentProvider");
  const currentProviderId = currentProvider
    ? providers?.[currentProvider]
    : undefined;
  const isSwypePrepaidProviderSelected =
    currentProviderId === SWYPE_PREPAID_PROVIDER_ID ||
    currentProvider?.trim().toLowerCase() === SWYPE_PREPAID_PROVIDER_NAME;

  useEffect(() => {
    if (!isSwypePrepaidProviderSelected) {
      return;
    }

    // TODO: Replace this one-off provider rule with a scalable provider-constraint mapping.
    setValue("mnpContractType", `${mnpContractType.prepaid.id}` as "2");
    resetField("mnpTime");
    resetField("mnpWishDate");
  }, [isSwypePrepaidProviderSelected, resetField, setValue]);

  const terminationIsDisabled = !(
    formValues?.mnpContractType?.toString() ===
    mnpContractType.postpaid.id?.toString()
  );

  const terminationBeforeContractEnd =
    formValues?.mnpTime?.toString() === mnpTime.before.id.toString();
  const wishdateSelectable =
    (customerData as any)?.wishdateSelectable === true ||
    (customerData as any)?.customData?.wishdateSelectable === true;
  const hasWishDateWhenNeeded =
    !terminationBeforeContractEnd ||
    !wishdateSelectable ||
    !!formValues?.mnpWishDate;

  // For missingInfo mode: button is enabled when "new" is selected, or when "keep" is selected and form is valid
  const completeButtonDisabled = missingInfo
    ? enableNumberPortingValue === "keep"
      ? !(
          formValues?.currentNumber &&
          formValues.currentProvider &&
          formValues.mnpContractType &&
          (formValues.mnpContractType?.toString() !==
            mnpContractType.postpaid.id?.toString() ||
            (formValues.mnpTime && hasWishDateWhenNeeded)) &&
          msisdnValidationStatus === "valid"
        )
      : enableNumberPortingValue === "new"
        ? false // "new" selected - button is always enabled
        : true // No option selected yet - button is disabled
    : !(
        dataContext.basket?.id &&
        formValues?.currentNumber &&
        formValues.currentProvider &&
        formValues.mnpContractType &&
        (formValues.mnpContractType?.toString() !==
          mnpContractType.postpaid.id?.toString() ||
          (formValues.mnpTime && hasWishDateWhenNeeded)) &&
        msisdnValidationStatus !== "invalid"
      );

  const onSubmit = (data: NumberPortingFormProps) => {
    if (dataContext.basket?.id) {
      const mnpData: MnpData & { portingWishdate?: string } = {
        // TODO: Temporary workaround to store the number
        portedPhoneNumber: {
          phone: data.currentNumber,
        },
        currentProvider: {
          id: providers?.[data.currentProvider] || -1,
          description: data.currentProvider,
        },
        mnpContractType:
          data.mnpContractType?.toString() ===
          mnpContractType.postpaid.id?.toString()
            ? mnpContractType.postpaid
            : mnpContractType.prepaid,
        mnpTime:
          data.mnpTime?.toString() === mnpTime.after.id.toString()
            ? mnpTime.after
            : mnpTime.before,
        msisdn: data.currentNumber, // TODO: Temporary workaround to store the number
      };
      if (
        wishdateSelectable &&
        data.mnpTime?.toString() === mnpTime.before.id.toString()
      ) {
        mnpData.portingWishdate = data.mnpWishDate;
      }

      // Track number porting analytics
      onboardingAnalytics.enableNumberPorting(
        data.currentProvider,
        data.mnpContractType === "1" ? "subscription" : "prepaid",
        data.mnpTime === "2" ? "after end of contract" : "early cancellation",
      );

      setMnpData({ basketId: dataContext.basket.id, index: 0, data: mnpData });
    } else {
      log(`
      dataContext.basket?.id: ${
        dataContext.basket?.id ? dataContext.basket?.id : "missing"
      }
      formData ${data ? data : "missing"}
      formData.currentNumber ${
        data.currentNumber ? data.currentNumber : "missing"
      }
      formData.currentProvider ${
        data.currentProvider ? data.currentProvider : "missing"
      }
      formData.mnpContractType ${
        data.mnpContractType ? data.mnpContractType : "missing"
      }
      formData.mnpTime ${data.mnpTime ? data.mnpTime : "missing"}
   `);
      withScope(function (scope) {
        scope.setTag("section", "onboarding");
        scope.setTag("onboardingStep", "numberporting");
        scope.setContext("numberPortingForm", {
          basketId: dataContext.basket?.id,
          formErrors: errors,
        });
        captureMessage(
          `Navigation in NumberPorting was triggered while it should be disabled.`,
        );
      });
    }
  };

  const navigateToNext = () => {
    handleSubmit(onSubmit)();
  };

  const submitNumberPorting = () => {
    const abortONP = formValues.enableNumberPorting === "new";

    // If "new" is selected, submit immediately without validation
    if (abortONP) {
      completeMnpData({
        simId: Number(simId),
        data: { abortONP: true },
      });

      return;
    }

    // If "keep" is selected, submit only when MSISDN is valid
    if (msisdnValidationStatus !== "valid") {
      return;
    }

    const mnpData: CompleteMnpData = {
      mnpData: {
        // TODO: Temporary workaround to store the number
        portedPhoneNumber: {
          phone: formValues.currentNumber,
        },
        currentProvider: {
          id: providers?.[formValues.currentProvider] || -1,
          description: formValues.currentProvider,
        },
        mnpContractType:
          formValues.mnpContractType?.toString() ===
          mnpContractType.postpaid.id?.toString()
            ? mnpContractType.postpaid
            : mnpContractType.prepaid,
        mnpTime:
          formValues.mnpTime?.toString() === mnpTime.after.id.toString()
            ? mnpTime.after
            : mnpTime.before,
        msisdn: formValues.currentNumber, // TODO: Temporary workaround to store the number
        ...(wishdateSelectable &&
        formValues.mnpTime?.toString() === mnpTime.before.id.toString()
          ? { portingWishdate: formValues.mnpWishDate }
          : {}),
      },
      abortONP: abortONP,
    };

    completeMnpData({
      simId: Number(simId),
      data: mnpData,
    });
  };

  return (
    <Step
      id="onboarding-number-porting"
      data-testid="onboarding-number-porting"
      headline={
        missingInfo
          ? ""
          : t("Onboarding.sections.rate-selection.header.rateFor") +
            " " +
            t(
              `Onboarding.sections.rate-selection.header.your.${device}`,
              t("Onboarding.sections.rate-selection.header.your.Andere"),
            )
      }
      headlinePrimary={
        missingInfo
          ? ""
          : t(
              `Common.devices.${deviceNameToValueMap[device]}`,
              t("Common.devices.Andere"),
            ).toString()
      }
      subheadline={
        missingInfo ? "" : t("Onboarding.sections.number-porting.subHeadline")
      }
      navigation={
        !missingInfo ? (
          <ButtonGroupFlexBox
            className="nav-button-container"
            forceButtonWidth
            primaryButton={
              <Button
                className="accent"
                disabled={completeButtonDisabled}
                onClick={() => navigateToNext()}
              >
                {t("Common.label.forward")}
              </Button>
            }
            secondaryButton={
              <Button
                className="accent inverted"
                onClick={() => navigate(`${basePath}/3/rate-selection`)}
              >
                {t("Common.label.back")}
              </Button>
            }
          />
        ) : (
          <div className="nav-button-container flex justify-end">
            <Button
              className="accent"
              disabled={completeButtonDisabled}
              onClick={() => submitNumberPorting()}
            >
              {t("Common.label.complete")}
            </Button>
          </div>
        )
      }
    >
      {(isLoadingProviders || isSettingMnpData) && <LoadingSpinner />}

      {missingInfo && (
        <div className="flex flex-col gap-2">
          <h3 className="mb-4 text-secondary-100">
            {t("portal:cockpit.managementTile.tabs.numberPorting.title")}
          </h3>
          <p>
            {t("portal:cockpit.managementTile.tabs.numberPorting.missingInfo")}
          </p>
          <p className="mt-10">
            {t(
              "portal:cockpit.managementTile.tabs.numberPorting.keepNumberQuestion",
            )}
          </p>
          <p className="text-sm text-secondary-100">
            <Trans
              i18nKey="portal:cockpit.managementTile.tabs.numberPorting.warning"
              components={[<span key="0" className="underline" />]}
            />
          </p>

          <fieldset className="form-field-wrapper mt-7">
            <RadioButton
              className="mb-5"
              name="enableNumberPorting"
              value="keep"
              errors={errors}
              register={register}
              labelComponent={
                <Trans
                  i18nKey="portal:cockpit.managementTile.tabs.numberPorting.keepNumber"
                  components={[<span key="0" className="font-semibold" />]}
                />
              }
            />
            <RadioButton
              name="enableNumberPorting"
              value="new"
              errors={errors}
              register={register}
              labelComponent={
                <Trans
                  i18nKey="portal:cockpit.managementTile.tabs.numberPorting.takeNewNumber"
                  components={[<span key="0" className="font-semibold" />]}
                />
              }
            />
          </fieldset>
        </div>
      )}
      {!missingInfo ? (
        <Accordion
          className="mt-10"
          label={t("Onboarding.sections.number-porting.numberPorting")}
          canOpen={false}
        >
          <p className="mt-4">
            <Trans i18nKey="Onboarding.sections.number-porting.accordion.subline" />
          </p>

          <div className="content-container">
            <div className="form-field-wrapper relative mt-4">
              <FormField<NumberPortingFormProps>
                id="telephone-number-input"
                autocomplete="tel"
                name="currentNumber"
                label={`1. ${t(
                  "Onboarding.sections.number-porting.currentNumber",
                )}`}
                errors={errors}
                register={register}
                type="text"
                placeholder={t(
                  "Onboarding.sections.number-porting.placeholder.currentNumber",
                )}
                onFocus={() => {
                  if (!watch("currentNumber")) {
                    setValue("currentNumber", numberPrefix);
                  }
                }}
                options={{
                  required: t("translation:label.validation.required"),
                  pattern: {
                    value: PHONE_REGEX,
                    message: t(
                      "Onboarding.sections.number-porting.numberFormat",
                    ),
                  },
                  onChange: (e: KeyboardEvent) => {
                    const input = e.target as HTMLInputElement;
                    const value = formatNumber(input.value, oldNumber);
                    setValue("currentNumber", value);

                    // Clear server error when user starts typing
                    if (errors.currentNumber?.type === "backendError") {
                      clearErrors("currentNumber");
                    }
                  },
                  onBlur: () => {
                    // Trigger validation when user leaves the field
                    const currentValue = watch("currentNumber");
                    if (
                      currentValue &&
                      PHONE_REGEX.test(currentValue) &&
                      msisdnValidationStatus !== "loading"
                    ) {
                      setMsisdnValidationStatus("loading");
                      const formattedMsisdn = formatMsisdnForApi(currentValue);
                      checkMsisdnAvailability({ msisdn: formattedMsisdn });
                    }
                  },
                }}
              />

              <div
                className={`prefix-coloring ${
                  watch("currentNumber") ? "" : "hidden"
                }`}
              ></div>

              {/* MSISDN validation error */}
              {msisdnError && (
                <ErrorMessage message={msisdnError} name="currentNumber" />
              )}
            </div>

            <div className="form-field-container form-field-wrapper mt-6">
              {providers && (
                <Controller<NumberPortingFormProps>
                  control={control}
                  name="currentProvider"
                  render={({ field }) => (
                    <Combobox
                      label={`2. ${t(
                        "Onboarding.sections.number-porting.currentProvider",
                      )}`}
                      field={field}
                      dataTestId="currentProviderCombobox"
                      options={Object.keys(providers)}
                      placeholder={t(
                        "Onboarding.sections.number-porting.placeholder.currentProvider",
                      )}
                    />
                  )}
                />
              )}
            </div>
          </div>

          <div className="content-container">
            <fieldset className="form-field-wrapper mt-7">
              <legend className="input-label">
                {`3. ${t(
                  "Onboarding.sections.number-porting.currentContractType",
                )}`}
              </legend>

              <RadioButton
                name="mnpContractType"
                value={`${mnpContractType.prepaid.id}`}
                errors={errors}
                register={register}
                label={t("Onboarding.sections.number-porting.prepaid")}
                options={{
                  onChange: () => {
                    resetField("mnpTime");
                  },
                }}
                className="mb-5"
              />

              <RadioButton
                name="mnpContractType"
                value={`${mnpContractType.postpaid.id}`} // mnpContractType: id 1 = postpaid | id 2 = prepaid
                errors={errors}
                register={register}
                label={t("Onboarding.sections.number-porting.postpaid")}
                options={{
                  disabled: isSwypePrepaidProviderSelected,
                }}
              />
            </fieldset>

            <fieldset
              className={clsx(
                "form-field-wrapper mt-6",
                terminationIsDisabled && "disabled",
              )}
              data-testid="termination"
            >
              <legend className="input-label">
                {`4. ${t("Onboarding.sections.number-porting.termination")}`}
              </legend>

              <p className="mb-2 text-sm">
                {t("Onboarding.sections.number-porting.steps")}
              </p>

              <RadioButton
                name="mnpTime"
                value={`${mnpTime.after.id}`}
                errors={errors}
                register={register}
                label={t(
                  "Onboarding.sections.number-porting.afterContractEnds",
                )}
                labelComponent={
                  <Trans
                    components={[
                      <span
                        key="recommended"
                        className="font-x-small m-2 text-primary-75"
                      />,
                    ]}
                    i18nKey="Onboarding.sections.number-porting.recommended"
                  />
                }
                options={{
                  disabled: terminationIsDisabled,
                  onChange: () => resetField("mnpWishDate"),
                }}
                className="mb-5"
              />

              <RadioButton
                name="mnpTime"
                value={`${mnpTime.before.id}`}
                errors={errors}
                register={register}
                label={t(
                  "Onboarding.sections.number-porting.beforeContractEnds",
                )}
                options={{
                  disabled: terminationIsDisabled,
                }}
              />

              {terminationBeforeContractEnd && (
                <div className="ml-[28px]">
                  <ErrorMessage
                    message={t(
                      "Onboarding.sections.number-porting.cost-warning",
                    ).toString()}
                    name={"mnpTime"}
                  />
                </div>
              )}

              {terminationBeforeContractEnd && wishdateSelectable && (
                <div className="ml-[28px] mr-2 mt-5">
                  <DateInput<NumberPortingFormProps>
                    id="mnp-wish-date-input"
                    name="mnpWishDate"
                    label={`5. ${t(
                      "Onboarding.sections.number-porting.chooseWishDate",
                    )}`}
                    autocomplete="off"
                    errors={errors}
                    register={register}
                    placeholder={t(
                      "Onboarding.sections.number-porting.placeholder.wishDate",
                    )}
                    value={watch("mnpWishDate")}
                    options={{
                      required: t("translation:label.validation.required"),
                      min: new Date().toISOString().split("T")[0],
                    }}
                  />
                </div>
              )}
            </fieldset>
          </div>
        </Accordion>
      ) : (
        enableNumberPortingValue === "keep" && (
          <>
            <div className="content-container">
              <div className="form-field-wrapper relative mt-4">
                <FormField<NumberPortingFormProps>
                  id="telephone-number-input"
                  autocomplete="tel"
                  name="currentNumber"
                  label={`1. ${t(
                    "Onboarding.sections.number-porting.currentNumber",
                  )}`}
                  errors={errors}
                  register={register}
                  type="text"
                  placeholder={t(
                    "Onboarding.sections.number-porting.placeholder.currentNumber",
                  )}
                  onFocus={() => {
                    if (!watch("currentNumber")) {
                      setValue("currentNumber", numberPrefix);
                    }
                  }}
                  options={{
                    required: t("translation:label.validation.required"),
                    pattern: {
                      value: PHONE_REGEX,
                      message: t(
                        "Onboarding.sections.number-porting.numberFormat",
                      ),
                    },
                    onChange: (e: KeyboardEvent) => {
                      const input = e.target as HTMLInputElement;
                      const value = formatNumber(input.value, oldNumber);
                      setValue("currentNumber", value);

                      // Clear server error when user starts typing
                      if (errors.currentNumber?.type === "backendError") {
                        clearErrors("currentNumber");
                      }
                    },
                    onBlur: () => {
                      // Trigger validation when user leaves the field
                      const currentValue = watch("currentNumber");
                      if (
                        currentValue &&
                        PHONE_REGEX.test(currentValue) &&
                        msisdnValidationStatus !== "loading"
                      ) {
                        setMsisdnValidationStatus("loading");
                        const formattedMsisdn =
                          formatMsisdnForApi(currentValue);
                        checkMsisdnAvailability({ msisdn: formattedMsisdn });
                      }
                    },
                  }}
                />

                <div
                  className={`prefix-coloring ${
                    watch("currentNumber") ? "" : "hidden"
                  }`}
                />

                {/* MSISDN validation error */}
                {msisdnError && (
                  <ErrorMessage message={msisdnError} name="currentNumber" />
                )}
              </div>

              <div className="form-field-container form-field-wrapper mt-6">
                {providers && (
                  <Controller<NumberPortingFormProps>
                    control={control}
                    name="currentProvider"
                    render={({ field }) => (
                      <Combobox
                        label={`2. ${t(
                          "Onboarding.sections.number-porting.currentProvider",
                        )}`}
                        field={field}
                        dataTestId="currentProviderCombobox"
                        options={Object.keys(providers)}
                        placeholder={t(
                          "Onboarding.sections.number-porting.placeholder.currentProvider",
                        )}
                      />
                    )}
                  />
                )}
              </div>
            </div>
            <div className="content-container">
              <fieldset className="form-field-wrapper mt-7">
                <legend className="input-label">
                  {`3. ${t(
                    "Onboarding.sections.number-porting.currentContractType",
                  )}`}
                </legend>

                <RadioButton
                  name="mnpContractType"
                  value={`${mnpContractType.prepaid.id}`}
                  errors={errors}
                  register={register}
                  label={t("Onboarding.sections.number-porting.prepaid")}
                  options={{
                    onChange: () => {
                      resetField("mnpTime");
                    },
                  }}
                  className="mb-5"
                />

                <RadioButton
                  name="mnpContractType"
                  value={`${mnpContractType.postpaid.id}`} // mnpContractType: id 1 = postpaid | id 2 = prepaid
                  errors={errors}
                  register={register}
                  label={t("Onboarding.sections.number-porting.postpaid")}
                  options={{
                    disabled: isSwypePrepaidProviderSelected,
                  }}
                />
              </fieldset>

              <fieldset
                className={clsx(
                  "form-field-wrapper mt-6",
                  terminationIsDisabled && "disabled",
                )}
                data-testid="termination"
              >
                <legend className="input-label">
                  {`4. ${t("Onboarding.sections.number-porting.termination")}`}
                </legend>

                <p className="mb-2 text-sm">
                  {t("Onboarding.sections.number-porting.steps")}
                </p>

                <RadioButton
                  name="mnpTime"
                  value={`${mnpTime.after.id}`}
                  errors={errors}
                  register={register}
                  label={t(
                    "Onboarding.sections.number-porting.afterContractEnds",
                  )}
                  labelComponent={
                    <Trans
                      components={[
                        <span
                          key="recommended"
                          className="font-x-small m-2 text-primary-75"
                        />,
                      ]}
                      i18nKey="Onboarding.sections.number-porting.recommended"
                    />
                  }
                  options={{
                    disabled: terminationIsDisabled,
                    onChange: () => resetField("mnpWishDate"),
                  }}
                  className="mb-5"
                />

                <RadioButton
                  name="mnpTime"
                  value={`${mnpTime.before.id}`}
                  errors={errors}
                  register={register}
                  label={t(
                    "Onboarding.sections.number-porting.beforeContractEnds",
                  )}
                  options={{
                    disabled: terminationIsDisabled,
                  }}
                />

                {terminationBeforeContractEnd && (
                  <div className="ml-[28px]">
                    <ErrorMessage
                      message={t(
                        "Onboarding.sections.number-porting.cost-warning",
                      ).toString()}
                      name={"mnpTime"}
                    />
                  </div>
                )}

                {terminationBeforeContractEnd && wishdateSelectable && (
                  <div className="ml-[28px] mr-2 mt-5">
                    <DateInput<NumberPortingFormProps>
                      id="mnp-wish-date-input"
                      name="mnpWishDate"
                      label={`5. ${t(
                        "Onboarding.sections.number-porting.chooseWishDate",
                      )}`}
                      autocomplete="off"
                      errors={errors}
                      register={register}
                      placeholder={t(
                        "Onboarding.sections.number-porting.placeholder.wishDate",
                      )}
                      value={watch("mnpWishDate")}
                      options={{
                        required: t("translation:label.validation.required"),
                        min: new Date().toISOString().split("T")[0],
                      }}
                    />
                  </div>
                )}
              </fieldset>
            </div>
          </>
        )
      )}
    </Step>
  );
};
